import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { usePageMeta } from '../hooks/usePageMeta';

export const PrivacyPolicyPage = () => {
  usePageMeta({
    title: 'Privacy Policy',
    description: 'Digitive Health privacy policy. Learn how we collect, use, and protect your personal information.',
    path: '/privacy-policy',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
      {/* Header */}
      <Header />

      {/* Content */}
      <section className="pt-28 pb-16">
        <div className="container-width">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-8">
              Privacy Policy
            </h1>
            <p className="text-muted-foreground mb-8">
              Last Updated: February 2025
            </p>

            <div className="prose prose-lg max-w-none text-muted-foreground space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">1. Introduction</h2>
                <p>
                  Digitive Health ("Company," "we," "us," or "our") is committed to protecting the privacy 
                  and security of your personal information. This Privacy Policy describes how we collect, use, 
                  disclose, and safeguard your information when you visit our website, use our healthcare staffing 
                  services, or interact with us in any way.
                </p>
                <p>
                  As a healthcare staffing company operating in the United States, we comply with all applicable 
                  federal and state privacy laws, including the Health Insurance Portability and Accountability 
                  Act (HIPAA) where applicable.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">2. Information We Collect</h2>
                <h3 className="text-xl font-medium text-foreground mb-3">Personal Information</h3>
                <p>We may collect the following types of personal information:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Contact information (name, email address, phone number, mailing address)</li>
                  <li>Professional credentials (licenses, certifications, education history)</li>
                  <li>Employment history and work experience</li>
                  <li>Social Security Number (for employment verification and tax purposes)</li>
                  <li>Background check information</li>
                  <li>Drug screening results</li>
                  <li>References and professional recommendations</li>
                  <li>Immigration status and work authorization documents</li>
                </ul>

                <h3 className="text-xl font-medium text-foreground mb-3 mt-6">Healthcare Facility Information</h3>
                <p>For our healthcare facility clients, we collect:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Facility name and contact information</li>
                  <li>Staffing requirements and job specifications</li>
                  <li>Billing and payment information</li>
                  <li>Authorized representative contact details</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">3. How We Use Your Information</h2>
                <p>We use collected information to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Match healthcare professionals with appropriate job opportunities</li>
                  <li>Verify professional credentials and conduct background checks</li>
                  <li>Process payroll and benefits administration</li>
                  <li>Communicate about job opportunities and assignments</li>
                  <li>Comply with legal and regulatory requirements</li>
                  <li>Improve our services and website functionality</li>
                  <li>Send marketing communications (with your consent)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">4. Information Sharing and Disclosure</h2>
                <p>We may share your information with:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Healthcare facilities seeking staffing services</li>
                  <li>Background check and credential verification services</li>
                  <li>Payroll and benefits administrators</li>
                  <li>Government agencies as required by law</li>
                  <li>Professional licensing boards for verification purposes</li>
                </ul>
                <p className="mt-4">
                  We do not sell your personal information to third parties for marketing purposes.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">5. Data Security</h2>
                <p>
                  We implement appropriate technical and organizational measures to protect your personal 
                  information, including:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Encryption of sensitive data in transit and at rest</li>
                  <li>Secure access controls and authentication</li>
                  <li>Regular security assessments and audits</li>
                  <li>Employee training on data protection</li>
                  <li>Physical security measures at our facilities</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">6. Your Rights</h2>
                <p>Depending on your state of residence, you may have the right to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access your personal information</li>
                  <li>Correct inaccurate information</li>
                  <li>Request deletion of your information</li>
                  <li>Opt-out of certain data processing activities</li>
                  <li>Receive a copy of your data in a portable format</li>
                </ul>
                <p className="mt-4">
                  California residents have additional rights under the California Consumer Privacy Act (CCPA).
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">7. Data Retention</h2>
                <p>
                  We retain your personal information for as long as necessary to fulfill the purposes for 
                  which it was collected, comply with legal obligations, resolve disputes, and enforce our 
                  agreements. Employment records are typically retained for a minimum of 7 years as required 
                  by federal and state regulations.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">8. Contact Us</h2>
                <p>
                  If you have questions about this Privacy Policy or wish to exercise your privacy rights, 
                  please contact us at:
                </p>
                <div className="mt-4 p-4 bg-secondary rounded-lg">
                  <p><strong>Digitive Health Inc.</strong></p>
                  <p>11501 Dublin Blvd STE 200</p>
                  <p>Dublin, CA 94568</p>
                  <p>Phone: (408) 935-8699</p>
                  <p>Email: sales@digitivehealth.com</p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">9. Changes to This Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of any material 
                  changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
                </p>
              </section>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
